import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../App";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CalendarDays, MapPin, Users, Euro, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Label } from "../components/ui/label";

const statusLabels = {
  nouvelle: "Nouvelle",
  a_analyser: "À analyser",
  acceptee: "Acceptée",
  soignant_a_attribuer: "Soignant à attribuer",
  soignant_attribue: "Soignant attribué",
  effectuee: "Effectuée",
  cloturee: "Clôturée",
  refusee: "Refusée",
};

function fullName(person) {
  return `${person?.first_name || ""} ${person?.last_name || ""}`.trim() ||
    person?.email ||
    "Soignant";
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(`${value}T12:00:00`);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("fr-BE");
}

function periods(creneaux = {}) {
  return ["matin", "midi", "soir"]
    .filter((key) => creneaux?.[key]?.selected)
    .map((key) => key.charAt(0).toUpperCase() + key.slice(1));
}

export default function AdminRemplacementsDomicile() {
  const { axiosInstance } = useAuth();

  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedCaregiver, setSelectedCaregiver] = useState({});
  const [statusFilter, setStatusFilter] = useState("");
  const [processing, setProcessing] = useState(null);

  const fetchData = async () => {
    try {
      const [remplacementsRes, usersRes] = await Promise.all([
        axiosInstance.get("/remplacements-domicile/admin"),
        axiosInstance.get("/users"),
      ]);

      setItems(Array.isArray(remplacementsRes.data) ? remplacementsRes.data : []);

      const caregivers = (Array.isArray(usersRes.data) ? usersRes.data : [])
        .filter(
          (u) =>
            ["nurse", "caregiver", "infirmier", "infirmière", "aide-soignant"].includes(u.role) &&
            u.status === "approved"
        )
        .map((u) => ({
          id: u.id || u._id,
          role: u.role,
          name: fullName(u),
        }));

      setUsers(caregivers);
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.detail ||
        "Impossible de charger les demandes."
      );
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filtered = useMemo(() => {
    return items.filter((item) => !statusFilter || item.statut === statusFilter);
  }, [items, statusFilter]);

  const accept = async (id) => {
    try {
      setProcessing(id);
      await axiosInstance.put(`/remplacements-domicile/${id}/accept`);
      toast.success("Demande acceptée.");
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'acceptation.");
    } finally {
      setProcessing(null);
    }
  };

  const reject = async (id) => {
    try {
      setProcessing(id);
      await axiosInstance.put(`/remplacements-domicile/${id}/reject`);
      toast.success("Demande refusée.");
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du refus.");
    } finally {
      setProcessing(null);
    }
  };

  const assign = async (item) => {
    const caregiverId = selectedCaregiver[item._id];

    if (!caregiverId) {
      toast.error("Sélectionnez un soignant.");
      return;
    }

    try {
      setProcessing(item._id);

      await axiosInstance.put(
        `/remplacements-domicile/${item._id}/assign`,
        {
          caregiver_type: "registered",
          soignant_id: caregiverId,
          soignant_externe_id: null,
        }
      );

      toast.success("Soignant attribué et prestations domicile créées.");

      setSelectedCaregiver((prev) => {
        const next = { ...prev };
        delete next[item._id];
        return next;
      });

      await fetchData();
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
        "Erreur lors de l'attribution."
      );
    } finally {
      setProcessing(null);
    }
  };

  const markComplete = async (id) => {
    try {
      setProcessing(id);
      await axiosInstance.put(`/remplacements-domicile/${id}/complete`);
      toast.success("Remplacement marqué comme effectué.");
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur.");
    } finally {
      setProcessing(null);
    }
  };

  const close = async (id) => {
    try {
      setProcessing(id);
      await axiosInstance.put(`/remplacements-domicile/${id}/close`);
      toast.success("Remplacement clôturé.");
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur.");
    } finally {
      setProcessing(null);
    }
  };

  return (
    <Layout title="Remplacements domicile">
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold text-[#315f63]">
            Demandes des titulaires
          </h2>
          <p className="mt-1 text-sm text-[#4f7d80]">
            Analysez, acceptez et attribuez les tournées à vos soignants.
          </p>
        </div>

        <Card className="border-[#d4ebeb] bg-[#f4fafa]">
          <CardContent className="p-4">
            <label className="text-sm font-medium text-[#315f63]">
              Filtrer par statut
            </label>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="mt-2 w-full rounded-md border border-[#d4ebeb] bg-white px-3 py-2 md:w-80"
            >
              <option value="">Tous</option>
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </CardContent>
        </Card>

        <div className="space-y-5">
          {filtered.length === 0 ? (
            <Card className="border-[#d4ebeb]">
              <CardContent className="p-8 text-center text-[#4f7d80]">
                Aucune demande de remplacement.
              </CardContent>
            </Card>
          ) : (
            filtered.map((item) => {
              const selectedPeriods = periods(item.creneaux);
              const canAccept = ["nouvelle", "a_analyser", "acceptee"].includes(item.statut);
              const canAssign = ["soignant_a_attribuer", "acceptee"].includes(item.statut);

              return (
                <Card key={item._id} className="border-[#d4ebeb] bg-white">
                  <CardHeader className="border-b border-[#e6f3f3] bg-[#f4fafa]">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <CardTitle className="text-[#315f63]">
                          {item.titulaire_name || "Titulaire"}
                        </CardTitle>
                        <p className="mt-1 text-sm text-[#4f7d80]">
                          {item.titulaire_email || ""}
                        </p>
                      </div>

                      <span className="w-fit rounded-full bg-[#e6f3f3] px-3 py-1 text-xs font-semibold text-[#315f63]">
                        {statusLabels[item.statut] || item.statut}
                      </span>
                    </div>
                  </CardHeader>

                  <CardContent className="p-5 space-y-5">
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                      <div className="rounded-lg bg-[#f4fafa] p-3">
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <CalendarDays size={14} />
                          Date
                        </div>
                        <p className="mt-1 font-semibold text-[#315f63]">
                          {formatDate(item.date)}
                        </p>
                      </div>

                      <div className="rounded-lg bg-[#f4fafa] p-3">
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <MapPin size={14} />
                          Zone
                        </div>
                        <p className="mt-1 font-semibold text-[#315f63]">
                          {item.zone || "—"}
                        </p>
                      </div>

                      <div className="rounded-lg bg-[#f4fafa] p-3">
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Users size={14} />
                          Patients
                        </div>
                        <p className="mt-1 font-semibold text-[#315f63]">
                          {item.nombre_patients || 0}
                        </p>
                      </div>

                      <div className="rounded-lg bg-[#f4fafa] p-3">
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Euro size={14} />
                          Estimation
                        </div>
                        <p className="mt-1 font-semibold text-[#315f63]">
                          {Number(item.montant_mutuelle_estime || 0).toFixed(2)} €
                        </p>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-[#315f63]">
                        Créneaux
                      </p>

                      <div className="mt-2 flex flex-wrap gap-2">
                        {selectedPeriods.map((period) => (
                          <span
                            key={period}
                            className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700"
                          >
                            {period}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-[#315f63]">
                        Patients
                      </p>

                      <div className="mt-2 overflow-x-auto rounded-xl border border-[#d4ebeb]">
                        <table className="min-w-[800px] w-full">
                          <thead className="bg-[#e6f3f3]">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs text-[#315f63]">
                                Patient
                              </th>
                              <th className="px-3 py-2 text-left text-xs text-[#315f63]">
                                Passage(s)
                              </th>
                              <th className="px-3 py-2 text-left text-xs text-[#315f63]">
                                Forfait
                              </th>
                              <th className="px-3 py-2 text-right text-xs text-[#315f63]">
                                Estimation
                              </th>
                            </tr>
                          </thead>

                          <tbody>
                            {(item.patients || []).map((patient, index) => (
                              <tr
                                key={`${item._id}-${index}`}
                                className="border-t border-[#e6f3f3]"
                              >
                                <td className="px-3 py-3 font-medium">
                                  {patient.prenom} {patient.nom}
                                </td>

                                <td className="px-3 py-3 text-sm">
                                  {(patient.passages || [])
                                    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
                                    .join(" + ")}
                                </td>

                                <td className="px-3 py-3 text-sm">
                                  {patient.forfait || "—"}
                                </td>

                                <td className="px-3 py-3 text-right font-semibold">
                                  {Number(patient.montant_estime || 0).toFixed(2)} €
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {item.soignant_name ? (
                      <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                        <p className="font-semibold text-green-800">
                          Soignant attribué : {item.soignant_name}
                        </p>
                      </div>
                    ) : canAssign ? (
                      <div className="rounded-xl border border-[#d4ebeb] bg-[#f4fafa] p-4">
                        <Label>Attribuer à un soignant</Label>

                        <div className="mt-2 flex flex-col gap-3 md:flex-row">
                          <select
                            value={selectedCaregiver[item._id] || ""}
                            onChange={(e) =>
                              setSelectedCaregiver((prev) => ({
                                ...prev,
                                [item._id]: e.target.value,
                              }))
                            }
                            className="flex-1 rounded-md border border-[#d4ebeb] bg-white px-3 py-2"
                          >
                            <option value="">— Choisir un soignant —</option>

                            {users.map((caregiver) => (
                              <option key={caregiver.id} value={caregiver.id}>
                                {caregiver.name} — {caregiver.role}
                              </option>
                            ))}
                          </select>

                          <Button
                            type="button"
                            disabled={processing === item._id}
                            onClick={() => assign(item)}
                            className="bg-[#6faeb2] hover:bg-[#5b979b] text-white"
                          >
                            Attribuer
                          </Button>
                        </div>
                      </div>
                    ) : null}

                    <div className="flex flex-wrap justify-end gap-2">
                      {canAccept && (
                        <Button
                          type="button"
                          disabled={processing === item._id}
                          onClick={() => accept(item._id)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <CheckCircle2 size={16} className="mr-2" />
                          Accepter
                        </Button>
                      )}

                      {["nouvelle", "a_analyser", "acceptee", "soignant_a_attribuer"].includes(item.statut) && (
                        <Button
                          type="button"
                          disabled={processing === item._id}
                          onClick={() => reject(item._id)}
                          className="bg-red-600 hover:bg-red-700 text-white"
                        >
                          <XCircle size={16} className="mr-2" />
                          Refuser
                        </Button>
                      )}

                      {item.statut === "soignant_attribue" && (
                        <Button
                          type="button"
                          disabled={processing === item._id}
                          onClick={() => markComplete(item._id)}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          Marquer comme effectuée
                        </Button>
                      )}

                      {item.statut === "effectuee" && (
                        <Button
                          type="button"
                          disabled={processing === item._id}
                          onClick={() => close(item._id)}
                          className="bg-[#315f63] hover:bg-[#24494c] text-white"
                        >
                          Clôturer
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </Layout>
  );
}
